import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, FolderOpen, MessageCircle, Plus, Search, LogOut, CheckSquare, X, Key } from 'lucide-react';
import { useNoteStore } from './stores/noteStore';
import { authService, AuthUser } from './services/auth';
import Sidebar from './components/Sidebar';
import NoteEditor from './components/NoteEditor';
import NoteList from './components/NoteList';
import SearchBar from './components/SearchBar';
import AIChat from './components/AIChat';
import LoginPage from './components/LoginPage';
import DragDropWrapper from './components/DragDropWrapper';
import ShareReceiver from './components/ShareReceiver';
import { Toaster } from './components/ui/Toaster';

// Mobile Bottom Navigation Component
const MobileBottomNav: React.FC<{
  onCreateNote: (type: 'text' | 'checklist' | 'password') => void;
  showFolders: boolean;
  setShowFolders: (show: boolean) => void;
}> = ({ onCreateNote, showFolders, setShowFolders }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Create Menu Overlay */}
      <AnimatePresence>
        {showCreateMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowCreateMenu(false)}
          />
        )}
      </AnimatePresence>

      {/* Create Menu */}
      <AnimatePresence>
        {showCreateMenu && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-20 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-2xl p-4 z-50 min-w-[220px]"
          >
            <div className="space-y-2">
              <button
                onClick={() => {
                  onCreateNote('text');
                  setShowCreateMenu(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-yellow-50 transition-colors"
              >
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">New Note</p>
                  <p className="text-xs text-gray-500">Write freely</p>
                </div>
              </button>
              
              <button
                onClick={() => {
                  onCreateNote('checklist');
                  setShowCreateMenu(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50 transition-colors"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">New Checklist</p>
                  <p className="text-xs text-gray-500">Track tasks</p>
                </div>
              </button>

              <button
                onClick={() => {
                  onCreateNote('password');
                  setShowCreateMenu(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-green-50 transition-colors"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Key className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">New Password</p>
                  <p className="text-xs text-gray-500">Store securely</p>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {/* Notes Tab */}
          <button
            onClick={() => {
              setShowFolders(false);
              navigate('/');
            }}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              isActive('/') && !showFolders ? 'text-yellow-600' : 'text-gray-500'
            }`}
          >
            <FileText className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Notes</span>
          </button>

          {/* Folders Tab */}
          <button
            onClick={() => setShowFolders(!showFolders)}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              showFolders ? 'text-yellow-600' : 'text-gray-500'
            }`}
          >
            <FolderOpen className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Folders</span>
          </button>

          {/* Create Button (Center) */}
          <button
            onClick={() => setShowCreateMenu(!showCreateMenu)}
            className="relative -mt-6 w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
          >
            <Plus className={`w-7 h-7 text-white transition-transform ${showCreateMenu ? 'rotate-45' : ''}`} />
          </button>

          {/* AI Chat Tab */}
          <button
            onClick={() => {
              setShowFolders(false);
              navigate('/ai-chat');
            }}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              isActive('/ai-chat') ? 'text-yellow-600' : 'text-gray-500'
            }`}
          >
            <MessageCircle className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">AI Chat</span>
          </button>

          {/* Search Tab */}
          <button
            onClick={() => {
              setShowFolders(false);
              navigate('/');
              // Focus search after navigation
              setTimeout(() => {
                const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
                searchInput?.focus();
              }, 100);
            }}
            className="flex flex-col items-center justify-center flex-1 h-full text-gray-500"
          >
            <Search className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Search</span>
          </button>
        </div>
      </div>
    </>
  );
};

// Mobile Folders Panel - Now a bottom sheet instead of full screen
const MobileFoldersPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-30"
        onClick={onClose}
      />
      {/* Panel */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-16 left-0 right-0 z-30 bg-white rounded-t-3xl max-h-[70vh] overflow-hidden shadow-2xl"
      >
        <div className="flex flex-col h-full">
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-notepad-text">Folders & Tags</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto pb-4">
            <Sidebar />
          </div>
        </div>
      </motion.div>
    </>
  );
};

// Share data interface
interface ShareData {
  files: File[];
  title?: string;
  text?: string;
}

function AppContent() {
  const { loadNotes, loadFolders, loadTags, createNote, setCurrentNote } = useNoteStore();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFolders, setShowFolders] = useState(false);
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle share target - check if we're on /share route
  useEffect(() => {
    const handleShareTarget = async () => {
      if (location.pathname === '/share' && 'launchQueue' in window) {
        // @ts-ignore - launchQueue is not in TypeScript types yet
        window.launchQueue.setConsumer(async (launchParams: any) => {
          if (launchParams.files && launchParams.files.length > 0) {
            const files: File[] = [];
            for (const fileHandle of launchParams.files) {
              const file = await fileHandle.getFile();
              files.push(file);
            }
            setShareData({ files });
          }
        });
      }
      
      // Check for files in service worker cache (for POST shares)
      if (location.pathname === '/share') {
        try {
          const cache = await caches.open('share-target-v2');
          const response = await cache.match('/share-data');
          
          if (response) {
            const data = await response.json();
            const files: File[] = [];
            
            // Convert base64 files back to File objects
            if (data.files && data.files.length > 0) {
              for (const fileData of data.files) {
                // Decode base64 to binary
                const binaryString = atob(fileData.data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: fileData.type });
                const file = new File([blob], fileData.name, { type: fileData.type });
                files.push(file);
              }
            }
            
            // Clear the cache
            await cache.delete('/share-data');
            
            if (files.length > 0 || data.title || data.text) {
              setShareData({ 
                files, 
                title: data.title || '', 
                text: data.text || '' 
              });
            }
          }
        } catch (err) {
          console.error('Error reading share cache:', err);
        }
      }
    };

    handleShareTarget();
  }, [location.pathname]);

  // Clear PIN session when app is hidden/switched (visibility change)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        // App is being switched away or closed - clear PIN session
        const { useSettingsStore } = await import('./stores/settingsStore');
        useSettingsStore.getState().clearPinSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let settingsUnsubscribe: (() => void) | null = null;

    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      setUser(user);
      setIsLoading(false);
      
      if (user) {
        try {
          // Load user data
          await loadNotes();
          await loadFolders();
          await loadTags();
          
          // Initialize settings sync with cloud
          const { useSettingsStore, subscribeToSettings } = await import('./stores/settingsStore');
          useSettingsStore.getState().setUserId(user.uid);
          settingsUnsubscribe = subscribeToSettings(user.uid);
        } catch (error) {
          console.error('Failed to load data:', error);
        }
      } else {
        // Clear settings user when logged out
        const { useSettingsStore } = await import('./stores/settingsStore');
        useSettingsStore.getState().setUserId(null);
        if (settingsUnsubscribe) {
          settingsUnsubscribe();
          settingsUnsubscribe = null;
        }
      }
    });

    return () => {
      unsubscribe();
      if (settingsUnsubscribe) {
        settingsUnsubscribe();
      }
    };
  }, [loadNotes, loadFolders, loadTags]);

  const handleCreateNote = async (type: 'text' | 'checklist' | 'password') => {
    const note = await createNote(type);
    if (note) {
      setCurrentNote(note);
    }
    // Navigate to the note on mobile
    if (window.innerWidth < 768) {
      navigate(`/note/${note.id}`);
    } else {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-notepad-yellow to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-notepad-text font-medium">Loading your notes...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={() => {}} />;
  }

  // Show share receiver if we have shared content
  if (shareData) {
    return (
      <ShareReceiver
        sharedFiles={shareData.files}
        sharedText={shareData.text}
        sharedTitle={shareData.title}
        onComplete={() => {
          setShareData(null);
          navigate('/');
        }}
        onCancel={() => {
          setShareData(null);
          navigate('/');
        }}
      />
    );
  }

  return (
    <DragDropWrapper>
      <div className="flex h-screen bg-gradient-to-br from-notepad-yellow to-yellow-50">
        <Toaster />
        
        {/* Desktop Sidebar */}
        <motion.div
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          className="hidden md:flex md:w-72 lg:w-80 bg-white shadow-lg border-r border-gray-200 flex-col"
        >
          <Sidebar />
        </motion.div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col pb-16 md:pb-0">
          {/* Header */}
          <motion.header
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white shadow-sm border-b border-gray-200 p-3 md:p-4"
          >
            <div className="flex items-center justify-between gap-3">
              {/* Mobile Logo */}
              <div className="md:hidden flex items-center gap-2">
                <img src="/favicon.svg" alt="Membank" className="w-6 h-6" />
                <span className="font-bold text-notepad-text">
                  Mem<span className="font-mono text-yellow-600">bank</span>
                </span>
              </div>
              
              {/* Search - Full width on desktop, hidden on mobile (use bottom nav) */}
              <div className="hidden md:block flex-1 max-w-md">
                <SearchBar />
              </div>
              
              {/* User Info & Sign Out */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 hidden sm:block truncate max-w-[120px]">
                  {user.displayName || user.email?.split('@')[0]}
                </span>
                <button
                  onClick={() => authService.signOut()}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  title="Sign Out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.header>

          {/* Content Area */}
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            <Routes>
              <Route path="/" element={
                <>
                  {/* Note List */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="w-full md:w-80 lg:w-96 border-b md:border-b-0 md:border-r border-gray-200 bg-white flex-shrink-0 overflow-hidden"
                  >
                    <NoteList />
                  </motion.div>
                  
                  {/* Note Editor */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex-1 overflow-hidden hidden md:block"
                  >
                    <NoteEditor />
                  </motion.div>
                </>
              } />
              
              <Route path="/note/:id" element={
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex-1 overflow-hidden"
                >
                  <NoteEditor />
                </motion.div>
              } />
              
              <Route path="/ai-chat" element={
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex-1 overflow-hidden"
                >
                  <AIChat />
                </motion.div>
              } />

              {/* Share target route - redirects to home, share data handled by ShareReceiver */}
              <Route path="/share" element={
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-10 h-10 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-notepad-text">Processing shared content...</p>
                  </div>
                </div>
              } />
            </Routes>
          </div>
        </div>        {/* Mobile Folders Panel */}
        <AnimatePresence>
          {showFolders && (
            <MobileFoldersPanel onClose={() => setShowFolders(false)} />
          )}
        </AnimatePresence>

        {/* Mobile Bottom Navigation */}
        <div className="md:hidden">
          <MobileBottomNav
            onCreateNote={handleCreateNote}
            showFolders={showFolders}
            setShowFolders={setShowFolders}
          />
        </div>
      </div>
    </DragDropWrapper>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
